import { getSubjectLabels } from '@/lib/settings';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { formatDate } from '../../../lib/dateFormat';
import { withdrawKuendigung } from '../../../lib/kuendigungen';
import { einwilligungenText, type Einwilligungen } from '../../../lib/einwilligungen';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';

/** Ein Vertrag aus ``GET /personen/{id}/vertraege`` (T-0155). */
export interface KundenVertrag {
  id: number;
  stand: 'aktiv' | 'gekuendigt' | 'inaktiv';
  lernender: string | null;
  subprofileId: string | null;
  naechsterTermin: string | null;
  unterrichtsform: string | null;
  turnus: string | null;
  lehrer: string | null;
  fach: string | null;
  standort: string | null;
  wochentag: number | null;
  startzeit: string | null;
  dauerMin: number | null;
  startDate: string | null;
  endDate: string | null;
  paket: { name: string | null; gesamt: number | null; verbraucht: number | null } | null;
  naechsteAufladung: {
    datum: string | null;
    paketName: string | null;
    termine: number | null;
    preisCents: number | null;
    raten: number | null;
  } | null;
  kuendigung: { id: number; zum: string | null } | null;
  einwilligungen?: Einwilligungen | null;
}

const WOCHENTAGE = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WOCHENTAGE_KURZ = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const euro = (cents: number) =>
  (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

function terminText(v: KundenVertrag): string {
  if (!v.wochentag || !v.startzeit) return '—';
  const [h, m] = v.startzeit.split(':').map(Number);
  const ende = new Date(2000, 0, 1, h, m + (v.dauerMin || 0));
  const bis = v.dauerMin
    ? ` - ${String(ende.getHours()).padStart(2, '0')}:${String(ende.getMinutes()).padStart(2, '0')}`
    : '';
  return `${WOCHENTAGE[v.wochentag]}, ${v.startzeit}${bis}`;
}

function naechsterTerminText(wert: string | null): string {
  if (!wert) return '—';
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return '—';
  return `${WOCHENTAGE_KURZ[d.getDay()]}, ${formatDate(d)}`;
}

function aufladungZeilen(a: NonNullable<KundenVertrag['naechsteAufladung']>): string {
  const termine = a.termine ? `${a.termine} Termine` : '';
  if (!a.preisCents) return termine;
  if (a.raten && a.raten > 1) {
    return `${termine} – ${a.raten} Raten je ${euro(Math.round(a.preisCents / a.raten))}`;
  }
  return `${termine} – Einmalzahlung ${euro(a.preisCents)}`;
}

function Zeile({ label, wert }: Readonly<{ label: string; wert: React.ReactNode }>) {
  return (
    <div className="flex items-start justify-between gap-4 py-1 text-sm">
      <span className="font-medium">{label}</span>
      <span className="text-right">{wert || '—'}</span>
    </div>
  );
}

type Props = {
  personId: number | string;
  /** Wechselt mit jeder Änderung an Verträgen, damit die Kacheln neu laden. */
  reloadKey?: number;
  onKuendigen?: (enrollmentId: number) => void;
  /** Auftragsbestätigung als PDF, falls es zur Anmeldung eine gibt. */
  auftragPdf?: (enrollmentId: number) => (() => void) | null;
};

export default function VertragsKacheln({ personId, reloadKey = 0, onKuendigen, auftragPdf }: Readonly<Props>) {
  const [vertraege, setVertraege] = useState<KundenVertrag[] | null>(null);
  const [offen, setOffen] = useState<KundenVertrag | null>(null);
  const [hebtAuf, setHebtAuf] = useState<number | null>(null);

  const laden = useCallback(async () => {
    try {
      const rows = await api.get<KundenVertrag[]>(`/api/personen/${personId}/vertraege`);
      setVertraege(Array.isArray(rows) ? rows : []);
    } catch {
      setVertraege([]);
    }
  }, [personId]);

  useEffect(() => {
    void laden();
  }, [laden, reloadKey]);

  const aufheben = async (v: KundenVertrag) => {
    if (!v.kuendigung) return;
    setHebtAuf(v.id);
    try {
      await withdrawKuendigung(v.kuendigung.id);
      toast.success('Kündigung aufgehoben');
      setOffen(null);
      await laden();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kündigung konnte nicht aufgehoben werden');
    } finally {
      setHebtAuf(null);
    }
  };

  if (vertraege === null) {
    return <p className="text-sm text-muted-foreground">Verträge werden geladen…</p>;
  }
  if (vertraege.length === 0) {
    return (
      <div className="rounded-lg border p-4 text-sm text-muted-foreground">
        Keine Verträge für diesen Kunden gefunden.
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2">
        {vertraege.map((v) => {
          const inaktiv = v.stand === 'inaktiv';
          return (
            <div key={v.id} className={cn('rounded-xl border bg-card p-4', inaktiv && 'bg-muted/40')}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-wrap gap-2">
                  {inaktiv ? (
                    <Badge variant="destructive">Kündigung wirksam</Badge>
                  ) : (
                    <Badge>Aktiv</Badge>
                  )}
                  {v.stand === 'gekuendigt' ? (
                    <Badge variant="destructive">
                      gekündigt zum {v.kuendigung?.zum ? formatDate(v.kuendigung.zum) : '—'}
                    </Badge>
                  ) : null}
                </div>
                <Button size="sm" variant="secondary" onClick={() => setOffen(v)}>
                  zum Vertrag
                </Button>
              </div>
              <div className={cn('mt-3', inaktiv && 'text-muted-foreground')}>
                <div className="text-base font-semibold">{v.lernender || '—'}</div>
                <div className="text-sm">
                  {inaktiv
                    ? `Vertragslaufzeit: ${v.startDate ? formatDate(v.startDate) : '—'} - ${
                        v.endDate ? formatDate(v.endDate) : '—'
                      }`
                    : `Nächster Termin: ${naechsterTerminText(v.naechsterTermin)}`}
                </div>
              </div>
              <div className={cn('mt-3 border-t pt-2', inaktiv && 'text-muted-foreground')}>
                <Zeile label="Unterricht" wert={[v.unterrichtsform, v.turnus].filter(Boolean).join(' / ')} />
                <Zeile label="Dozent" wert={v.lehrer} />
                <Zeile label={getSubjectLabels().singular} wert={v.fach} />
                <Zeile label="Termin" wert={terminText(v)} />
              </div>
              {v.stand === 'aktiv' && v.paket ? (
                <div className="mt-3">
                  <Zeile
                    label="Aktives Paket"
                    wert={
                      <>
                        <div className="font-medium">{v.paket.name}</div>
                        <div className="text-muted-foreground">
                          {v.paket.verbraucht ?? 0} von {v.paket.gesamt ?? 0} Terminen verbraucht
                        </div>
                      </>
                    }
                  />
                </div>
              ) : null}
              {v.stand === 'aktiv' && v.naechsteAufladung ? (
                <div className="mt-2">
                  <Zeile
                    label="Nächste Aufladung"
                    wert={
                      <>
                        <div>{v.naechsteAufladung.datum ? formatDate(v.naechsteAufladung.datum) : 'wenn das Paket verbraucht ist'}</div>
                        <div className="font-medium">{v.naechsteAufladung.paketName}</div>
                        <div className="text-muted-foreground">{aufladungZeilen(v.naechsteAufladung)}</div>
                      </>
                    }
                  />
                </div>
              ) : null}
              {v.stand === 'gekuendigt' && v.kuendigung ? (
                <Button className="mt-3" size="sm" disabled={hebtAuf === v.id} onClick={() => void aufheben(v)}>
                  Kündigung aufheben
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>

      <Dialog open={offen !== null} onOpenChange={(o) => !o && setOffen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vertrag · {offen?.lernender || '—'}</DialogTitle>
            <DialogDescription>
              {[offen?.fach, offen?.standort].filter(Boolean).join(' · ') || 'Anmeldung'} · Anmeldung #{offen?.id}
            </DialogDescription>
          </DialogHeader>
          {offen ? (
            <div>
              <Zeile label="Unterricht" wert={[offen.unterrichtsform, offen.turnus].filter(Boolean).join(' / ')} />
              <Zeile label="Dozent" wert={offen.lehrer} />
              <Zeile label="Termin" wert={terminText(offen)} />
              <Zeile label="Beginn" wert={offen.startDate ? formatDate(offen.startDate) : null} />
              <Zeile label="Ende" wert={offen.endDate ? formatDate(offen.endDate) : 'unbefristet'} />
              <Zeile label="Nächster Termin" wert={naechsterTerminText(offen.naechsterTermin)} />
              {offen.paket ? (
                <Zeile
                  label="Paket"
                  wert={`${offen.paket.name} · ${offen.paket.verbraucht ?? 0} von ${offen.paket.gesamt ?? 0} verbraucht`}
                />
              ) : null}
              {offen.einwilligungen ? (
                <Zeile label="Einwilligungen" wert={einwilligungenText(offen.einwilligungen)} />
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            {offen && auftragPdf?.(offen.id) ? (
              <Button variant="outline" onClick={() => auftragPdf(offen.id)?.()}>
                Auftragsbestätigung (PDF)
              </Button>
            ) : null}
            {offen?.stand === 'gekuendigt' ? (
              <Button disabled={hebtAuf === offen.id} onClick={() => void aufheben(offen)}>
                Kündigung aufheben
              </Button>
            ) : null}
            {offen?.stand === 'aktiv' && onKuendigen ? (
              <Button
                variant="destructive"
                onClick={() => {
                  onKuendigen(offen.id);
                  setOffen(null);
                }}
              >
                Kündigen
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
